// Debounced audience-count query for the wizard. Caches by filter-hash so
// stepping back and forth across the wizard doesn't re-query. See UX doc §6.4.

import { useEffect, useMemo, useState } from 'react';
import { usePreviewAudienceQuery } from '../campaignsApi';
import { LIMITS } from '../constants';

const hashFilters = (filters = [], excludeUserIds = []) => {
  return JSON.stringify({ filters, excludeUserIds });
};

export const useAudiencePreview = ({ filters, excludeUserIds = [] }) => {
  const [debouncedHash, setDebouncedHash] = useState(() => hashFilters(filters, excludeUserIds));

  const hash = useMemo(() => hashFilters(filters, excludeUserIds), [filters, excludeUserIds]);

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedHash(hash), LIMITS.AUDIENCE_PREVIEW_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [hash]);

  // Parse back so RTK Query sees the same object shape on every identical hash —
  // stable reference deduplication is handled by the cache key.
  const parsed = useMemo(() => JSON.parse(debouncedHash), [debouncedHash]);

  const { data, isFetching, isLoading, isError } = usePreviewAudienceQuery(parsed, {
    // Don't block the wizard's Continue button — polling is purely advisory.
    refetchOnMountOrArgChange: false,
  });

  return {
    count: data?.count ?? null,
    matchCount: data?.matchCount ?? null,
    suppressedForConsent: data?.suppressedForConsent ?? 0,
    sampleUsers: data?.sampleUsers ?? [],
    isLoading,
    isFetching,
    isError,
    isStale: hash !== debouncedHash,
  };
};
